import { useState } from "react";
import { motion } from "framer-motion";
import {
  Building2,
  Camera,
  Check,
  CircleAlert,
  CircleCheck,
  CircleDashed,
  CloudRain,
  Download,
  FileText,
  Film,
  Flame,
  Image as ImageIcon,
  Lock,
  Plus,
  ShieldHalf,
  Smartphone,
} from "lucide-react";
import { CLUB, FOCUS_BY_ID, MIN_GROUP, SEASON, TARGETS, euro, fmt } from "./demoData";
import {
  BTN_SMALL,
  ColumnChart,
  EASE,
  HBars,
  Kicker,
  Panel,
  ProgressLine,
  Ring,
  Segmented,
  SourceBadge,
} from "./ui";

const PERSPECTIVES = [
  { value: "unternehmen", label: "Sicht Unternehmen", icon: Building2 },
  { value: "verein", label: "Sicht Verein", icon: ShieldHalf },
];

const POST_ICONS = { Beitrag: ImageIcon, Story: CircleDashed, Reel: Film };

const Chip = ({ children }) => (
  <span className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-xs font-medium text-white/65">
    {children}
  </span>
);

const LevelTitle = ({ no, title, text }) => (
  <div className="pt-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
    <div className="flex items-baseline gap-4">
      <span className="font-display font-black text-3xl gradient-text">{no}</span>
      <h3 className="font-display font-bold text-2xl md:text-3xl text-white">{title}</h3>
    </div>
    <p className="text-sm text-white/45 sm:text-right max-w-md">{text}</p>
  </div>
);

export const Legend = () => (
  <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-white/45">
    <span className="inline-flex items-center gap-2">
      <SourceBadge kind="gemessen" /> von MatchGate erfasst
    </span>
    <span className="inline-flex items-center gap-2">
      <SourceBadge kind="angabe" proof /> eingetragen, mit Beleg
    </span>
    <span className="inline-flex items-center gap-2">
      <SourceBadge kind="schaetzung" /> hochgerechnet
    </span>
  </div>
);

const NewEntryBanner = ({ entry, game }) => {
  const text =
    entry.status === "ausgefallen"
      ? `Heimspiel ${entry.n} gegen ${game.opponent} ist als ausgefallen gemeldet. Die Leistungen für diesen Termin zählen nicht als fällig.`
      : `Heimspiel ${entry.n} gegen ${game.opponent}${
          entry.spectators != null ? `, ${fmt(entry.spectators)} Zuschauer (${entry.bucket || entry.method})` : ""
        }. Das neue Spiel ist in den Diagrammen hervorgehoben.`;
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: EASE }}
      className="flex items-start gap-3 rounded-2xl border border-white/15 bg-white/[0.04] px-5 py-4"
    >
      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-orange to-brand-blue">
        <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
      </span>
      <p className="text-sm leading-relaxed text-white/70">
        <span className="font-semibold text-white">Gerade über die Erfassung eingetragen: </span>
        {text}
      </p>
    </motion.div>
  );
};

export const Highlights = ({ items }) => (
  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
    {items.map((k, i) => (
      <motion.div
        key={k.key}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: i * 0.06, duration: 0.6, ease: EASE }}
        className={`relative overflow-hidden rounded-2xl border backdrop-blur-xl p-5 flex flex-col ${
          i === 0 ? "border-white/20" : "border-white/10"
        }`}
      >
        {i === 0 && (
          <div className="pointer-events-none absolute -top-12 -right-10 w-32 h-32 rounded-full bg-brand-orange/20 blur-[60px]" />
        )}
        <div className="relative flex items-start justify-between gap-2">
          <span className="text-xs uppercase tracking-wider text-white/45">{k.label}</span>
          {i === 0 && (
            <span className="text-[10px] uppercase tracking-wider font-bold text-brand-orange whitespace-nowrap">
              Schwerpunkt
            </span>
          )}
        </div>
        <div className="relative mt-3 font-display font-bold text-3xl text-white tabular-nums">{k.value}</div>
        <div className="relative mt-1 text-xs leading-relaxed text-white/45 flex-1">{k.sub}</div>
        <div className="relative mt-4">
          <SourceBadge kind={k.source} by={k.by} proof={k.proof} />
        </div>
      </motion.div>
    ))}
  </div>
);

const DeliveryRow = ({ item }) => {
  const Icon = item.icon;
  const ok = item.done >= item.due;
  const open = item.due - item.done;
  return (
    <li className="py-4 grid grid-cols-[auto_minmax(0,1fr)] sm:grid-cols-[auto_minmax(0,1fr)_8.5rem_6.5rem] items-center gap-x-4 gap-y-2">
      <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-white/[0.05] border border-white/10">
        <Icon className="w-4 h-4 text-white/70" />
      </span>
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-sm font-semibold text-white">
          <span className="leading-snug">{item.title}</span>
          {item.proof && <Camera className="w-3.5 h-3.5 shrink-0 text-white/40" aria-label="mit Foto belegt" />}
          {item.source === "gemessen" && (
            <span className="hidden md:inline text-[10px] uppercase tracking-wider font-bold text-[#6FB1FF]">
              automatisch
            </span>
          )}
        </div>
        <div className="mt-0.5 text-xs text-white/45">{item.detail}</div>
      </div>
      <div className="col-start-2 sm:col-start-auto">
        {item.total > 1 && (
          <>
            <div className="mb-1.5 text-[11px] text-white/45 tabular-nums">
              {item.done} von {item.total}
            </div>
            <ProgressLine value={item.done / item.total} tone={ok ? "gradient" : "warn"} />
          </>
        )}
      </div>
      <span
        className={`col-start-2 sm:col-start-auto sm:justify-self-end inline-flex items-center gap-1.5 text-xs font-semibold ${
          ok ? "text-[#6FB1FF]" : "text-brand-orange"
        }`}
      >
        {ok ? <CircleCheck className="w-3.5 h-3.5" /> : <CircleAlert className="w-3.5 h-3.5" />}
        {ok ? "im Plan" : `${open} offen`}
      </span>
    </li>
  );
};

const Delivered = ({ data }) => {
  const { fulfilment, deliveries, sponsor } = data;
  return (
    <Panel className="p-6 md:p-8">
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
        <div className="lg:col-span-4 flex flex-col items-center lg:items-start text-center lg:text-left">
          <div className="relative w-40 h-40">
            <Ring value={fulfilment.pct / 100} size={160} />
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="flex items-baseline font-display font-bold text-[1.75rem] leading-none tracking-tight text-white tabular-nums">
                {fulfilment.pct}
                <span className="ml-0.5 text-base font-semibold text-white/55">%</span>
              </span>
              <span className="mt-1.5 text-[10px] uppercase tracking-[0.18em] text-white/40">erbracht</span>
            </div>
          </div>
          <p className="mt-6 text-sm leading-relaxed text-white/55">
            {fulfilment.done} von {fulfilment.due} bisher fälligen Leistungen sind erbracht. Fällig ist, was laut
            Vereinbarung bis heute passiert sein sollte.
          </p>
          <div className="mt-4">
            <SourceBadge kind="angabe" by="Verein" proof />
          </div>
        </div>

        <div className="lg:col-span-8">
          <Kicker>Leistungen des Vereins</Kicker>
          <ul className="mt-2 divide-y divide-white/[0.07]">
            {deliveries.club.map((item) => (
              <DeliveryRow key={item.id} item={item} />
            ))}
            {deliveries.club.length === 0 && (
              <li className="py-4 text-sm text-white/45">Im Setup ist noch keine Leistung des Vereins gewählt.</li>
            )}
          </ul>
          <Kicker className="mt-8">Leistungen von {sponsor.name}</Kicker>
          <ul className="mt-2 divide-y divide-white/[0.07]">
            {deliveries.sponsor.map((item) => (
              <DeliveryRow key={item.id} item={item} />
            ))}
          </ul>
        </div>
      </div>
    </Panel>
  );
};

const Photos = ({ fotos, perspective }) => {
  const [hint, setHint] = useState(false);
  return (
    <Panel className="p-6 md:p-8">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <Kicker>Fotos für eure Kanäle</Kicker>
          <p className="mt-2 text-sm text-white/55">
            {fotos.delivered} von {fotos.qty} geliefert, {fotos.downloaded} davon heruntergeladen
          </p>
        </div>
        <div className="flex items-center gap-3">
          <SourceBadge kind="gemessen" />
          {perspective === "unternehmen" ? (
            <button type="button" onClick={() => setHint((v) => !v)} className={BTN_SMALL}>
              <Download className="w-3.5 h-3.5" />
              Alle herunterladen
            </button>
          ) : (
            <button type="button" onClick={() => setHint((v) => !v)} className={BTN_SMALL}>
              <Camera className="w-3.5 h-3.5" />
              Fotos hochladen
            </button>
          )}
        </div>
      </div>
      {hint && <p className="mt-3 text-xs text-white/45">In der Demo gibt es keine echten Dateien.</p>}
      <div className="mt-6 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        {fotos.captions.map((caption, i) => (
          <motion.div
            key={caption}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: i * 0.04, duration: 0.5, ease: EASE }}
            className="relative aspect-square overflow-hidden rounded-xl border border-white/10"
          >
            <div
              className={`absolute inset-0 ${
                i % 2 ? "bg-gradient-to-br from-brand-blue/25 to-brand-orange/10" : "bg-gradient-to-tr from-brand-orange/20 to-brand-blue/15"
              }`}
            />
            <div className="relative h-full flex flex-col items-center justify-center gap-1.5 p-2 text-center">
              <Camera className="w-4 h-4 text-white/45" />
              <span className="text-[10px] leading-tight text-white/60">{caption}</span>
            </div>
          </motion.div>
        ))}
        {Array.from({ length: Math.max(0, Math.min(6, fotos.qty - fotos.delivered)) }).map((_, i) => (
          <div
            key={`open-${i}`}
            className="aspect-square rounded-xl border border-dashed border-white/10 flex items-center justify-center text-[10px] text-white/25"
          >
            folgt
          </div>
        ))}
      </div>
    </Panel>
  );
};

const Spectators = ({ data }) => {
  const { spectators, games } = data;
  if (!spectators) {
    return (
      <Panel className="lg:col-span-8 p-6 md:p-8 flex flex-col justify-center">
        <Kicker>Zuschauer</Kicker>
        <p className="mt-3 max-w-lg text-sm leading-relaxed text-white/55">
          In diesem Sponsoring werden keine Zuschauer erfasst. Das ist erlaubt, nur fehlt dann die Grundlage für die
          Hochrechnung der Kontakte vor Ort.
        </p>
      </Panel>
    );
  }
  const items = games.map((g) => ({
    key: `g${g.n}`,
    label: String(g.n),
    value: g.status === "gespielt" ? g.spectators ?? null : null,
    state: g.status === "geplant" ? "planned" : g.status === "ausgefallen" ? "cancelled" : null,
    highlight: g.status === "gespielt",
    entered: g.entered,
    title:
      g.status === "gespielt"
        ? `Heimspiel ${g.n} gegen ${g.opponent}: ${g.spectators ?? "keine Angabe"} Zuschauer (${g.method || "ohne Angabe"})`
        : g.status === "ausgefallen"
        ? `Heimspiel ${g.n} gegen ${g.opponent}: ausgefallen`
        : `Heimspiel ${g.n} gegen ${g.opponent}: ${g.weekday} ${g.date}`,
    marker:
      g.context === "Derby" ? (
        <Flame className="w-3.5 h-3.5 text-brand-orange/80" aria-label="Derby" />
      ) : g.context === "Regen" ? (
        <CloudRain className="w-3.5 h-3.5 text-[#6FB1FF]" aria-label="Regen" />
      ) : null,
  }));
  const methods = Object.entries(spectators.methods)
    .map(([m, n]) => `${n}× ${m}`)
    .join(" · ");
  return (
    <Panel className="lg:col-span-8 p-6 md:p-8">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
        <div>
          <Kicker>Zuschauer je Heimspiel, {CLUB.team}</Kicker>
          <div className="mt-3 flex items-baseline gap-3">
            <span className="font-display font-bold text-4xl text-white tabular-nums">{fmt(spectators.firstTeam)}</span>
            <span className="text-sm text-white/45">Ø {spectators.avg} je Spiel</span>
          </div>
        </div>
        <SourceBadge kind="angabe" by="Verein" />
      </div>
      <ColumnChart
        items={items}
        markers
        height={180}
        ariaLabel={`Zuschauer der ${CLUB.team} je Heimspiel, bisher ${fmt(spectators.firstTeam)} insgesamt`}
      />
      <div className="mt-2 text-center text-[10px] uppercase tracking-[0.2em] text-white/30">Heimspiel</div>
      <div className="mt-6 pt-5 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs text-white/45">
        <span>Grundlage: {methods}</span>
        <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="inline-flex items-center gap-1.5">
            <Flame className="w-3.5 h-3.5 text-brand-orange/80" /> Derby
          </span>
          <span className="inline-flex items-center gap-1.5">
            <CloudRain className="w-3.5 h-3.5 text-[#6FB1FF]" /> Regen
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-2.5 rounded-sm border border-dashed border-white/30" /> geplant
          </span>
        </span>
      </div>
      {spectators.youth > 0 && (
        <p className="mt-4 text-xs leading-relaxed text-white/45">
          Dazu kommen {fmt(spectators.youth)} Zuschauer der weiteren Mannschaften an {spectators.weekends}{" "}
          Wochenenden, eingetragen über Helfer-Links der Trainerinnen und Trainer.
        </p>
      )}
    </Panel>
  );
};

const Contacts = ({ contacts }) => (
  <Panel className="lg:col-span-4 p-6 md:p-8 flex flex-col">
    <div className="flex items-start justify-between gap-3">
      <Kicker>Kontakte vor Ort</Kicker>
      <SourceBadge kind="schaetzung" />
    </div>
    {contacts ? (
      <>
        <div className="mt-3 font-display font-bold text-4xl text-white tabular-nums">≈ {fmt(contacts.total)}</div>
        <div className="mt-6 text-xs uppercase tracking-wider text-white/35">So gerechnet</div>
        <ul className="mt-3 space-y-3">
          {contacts.parts.map((p) => (
            <li key={p.label} className="flex items-start justify-between gap-3 text-sm">
              <span>
                <span className="block text-white/75">{p.label}</span>
                <span className="block text-[11px] text-white/35">{p.note}</span>
              </span>
              <span className="font-semibold tabular-nums text-white">{fmt(p.value)}</span>
            </li>
          ))}
        </ul>
        <p className="mt-auto pt-6 text-xs leading-relaxed text-white/40">
          Kontakte sind keine Personen: Viele Eltern und Fans kommen jede Woche wieder. Genau diese Wiederholung macht
          euren Namen vor Ort bekannt.
        </p>
      </>
    ) : (
      <p className="mt-3 text-sm leading-relaxed text-white/50">
        Ohne Zuschauerzahlen oder Präsenz am Platz gibt es keine Hochrechnung. Sie entsteht, sobald im Setup beides
        gewählt ist.
      </p>
    )}
  </Panel>
);

const Social = ({ social }) => (
  <Panel className="lg:col-span-12 p-6 md:p-8">
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
      <div className="lg:col-span-4">
        <div className="flex items-start justify-between gap-3">
          <Kicker>Partnerbeiträge auf Instagram</Kicker>
          <SourceBadge kind="gemessen" />
        </div>
        <div className="mt-3 font-display font-bold text-4xl text-white tabular-nums">{fmt(social.views)}</div>
        <div className="text-sm text-white/45">
          Aufrufe in {social.done} von {social.qty} vereinbarten Beiträgen
        </div>
        <div className="mt-6 grid grid-cols-2 gap-3">
          <div className="rounded-2xl border border-white/10 px-4 py-3">
            <div className="text-[11px] uppercase tracking-wider text-white/40">Interaktionen</div>
            <div className="mt-1 font-display font-bold text-xl tabular-nums">{fmt(social.interactions)}</div>
          </div>
          <div className="rounded-2xl border border-white/10 px-4 py-3">
            <div className="text-[11px] uppercase tracking-wider text-white/40">Rate</div>
            <div className="mt-1 font-display font-bold text-xl tabular-nums">
              {social.rate.toLocaleString("de-DE", { maximumFractionDigits: 1 })} %
            </div>
          </div>
        </div>
        <p className="mt-5 text-xs leading-relaxed text-white/40">
          Automatisch über die Schnittstelle erkannt, weil der Verein euch im Beitrag markiert. Zahlen von Stories
          werden innerhalb von 24 Stunden abgeholt, danach gibt Instagram sie nicht mehr heraus.
        </p>
      </div>
      <div className="lg:col-span-8">
        {social.posts.length ? (
          <ul className="divide-y divide-white/[0.07]">
            {social.posts.map((p, i) => {
              const Icon = POST_ICONS[p.type];
              return (
                <motion.li
                  key={p.title}
                  initial={{ opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05, duration: 0.5, ease: EASE }}
                  className="py-3.5 flex items-center gap-4"
                >
                  <span className="flex items-center justify-center w-11 h-11 shrink-0 rounded-xl border border-white/10 bg-origin-border bg-gradient-to-br from-brand-orange/20 to-brand-blue/20">
                    <Icon className="w-4 h-4 text-white/70" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-white truncate">{p.title}</div>
                    <div className="text-xs text-white/40">
                      {p.type} · {p.date}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold tabular-nums text-white">{fmt(p.views)}</div>
                    <div className="text-[11px] text-white/40">Aufrufe</div>
                  </div>
                  <div className="hidden sm:block w-24 text-right">
                    <div className="text-sm font-semibold tabular-nums text-white">{fmt(p.interactions)}</div>
                    <div className="text-[11px] text-white/40">Interaktionen</div>
                  </div>
                </motion.li>
              );
            })}
          </ul>
        ) : (
          <p className="text-sm text-white/50">Der erste vereinbarte Beitrag ist noch nicht fällig.</p>
        )}
      </div>
    </div>
  </Panel>
);

const QrScans = ({ qr }) => (
  <>
    <Panel className="lg:col-span-8 p-6 md:p-8">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
        <div>
          <Kicker>QR-Scans je Kalenderwoche</Kicker>
          <div className="mt-3 flex items-baseline gap-3">
            <span className="font-display font-bold text-4xl text-white tabular-nums">{fmt(qr.total)}</span>
            <span className="text-sm text-white/45">führen auf: {TARGETS[qr.target]}</span>
          </div>
        </div>
        <SourceBadge kind="gemessen" />
      </div>
      <ColumnChart items={qr.series} height={170} ariaLabel={`QR-Scans je Kalenderwoche, insgesamt ${qr.total}`} />
      <div className="mt-2 text-center text-[10px] uppercase tracking-[0.2em] text-white/30">Kalenderwoche</div>
      <div className="mt-6 pt-5 border-t border-white/10 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-white/45">
        <span className="inline-flex items-center gap-2">
          <span className="w-3 h-3 rounded-sm bg-gradient-to-t from-brand-orange/50 to-brand-blue/80" /> Woche mit
          Heimspiel
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="w-3 h-3 rounded-sm bg-white/[0.12]" /> ohne Heimspiel
        </span>
        <span className="sm:ml-auto">Gezählt ohne IP-Adresse, Vorschau-Abrufe und Bots herausgefiltert.</span>
      </div>
    </Panel>
    <Panel className="lg:col-span-4 p-6 md:p-8">
      <Kicker>Nach Platzierung</Kicker>
      <div className="mt-6">
        <HBars items={qr.placements} />
      </div>
      <p className="mt-6 text-xs leading-relaxed text-white/40">
        {qr.placements.length > 1
          ? "Jede Platzierung hat einen eigenen Code. So seht ihr, welche am meisten bringt."
          : "Jede weitere Platzierung bekäme einen eigenen Code, damit sich vergleichen lässt, was mehr bringt."}
        {qr.target === "website" && " Die Besuche erscheinen zusätzlich in eurer eigenen Webanalyse."}
      </p>
    </Panel>
  </>
);

const Channels = ({ channels, clicks, wide }) => (
  <Panel className={`${wide ? "lg:col-span-12" : "lg:col-span-6"} p-6 md:p-8`}>
    <div className="flex items-start justify-between gap-3">
      <Kicker>Link-Klicks nach Kanal</Kicker>
      <SourceBadge kind="gemessen" />
    </div>
    <div className="mt-3 font-display font-bold text-4xl text-white tabular-nums">{fmt(clicks)}</div>
    <div className="mt-7">
      <HBars items={channels} />
    </div>
  </Panel>
);

const Results = ({ data, onReport, wide }) => {
  const { results, resultsLocked, sponsor, perspective } = data;
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState(results[0]?.key);
  const [amount, setAmount] = useState("1");
  const [error, setError] = useState("");
  const selected = results.find((r) => r.key === key) ? key : results[0]?.key;

  const submit = (e) => {
    e.preventDefault();
    const value = amount.trim();
    if (!/^\d+$/.test(value) || Number(value) < 1 || Number(value) > 99) {
      setError("Trag eine ganze Zahl zwischen 1 und 99 ein.");
      return;
    }
    onReport(selected, Number(value));
    setOpen(false);
    setAmount("1");
    setError("");
  };

  return (
    <Panel className={`${wide ? "lg:col-span-12" : "lg:col-span-6"} p-6 md:p-8 flex flex-col`}>
      <div className="flex items-start justify-between gap-3">
        <Kicker>Ergebnisse, gemeldet von {sponsor.name}</Kicker>
        <SourceBadge kind="angabe" by="Unternehmen" />
      </div>

      {resultsLocked ? (
        <div className="mt-6 flex items-start gap-3 rounded-2xl border border-dashed border-white/15 p-5">
          <Lock className="w-4 h-4 mt-0.5 shrink-0 text-white/50" />
          <p className="text-sm leading-relaxed text-white/55">
            {sponsor.name} hat die eigenen Ergebnisse für den Verein nicht freigegeben. Das lässt sich im Setup
            jederzeit ändern.
          </p>
        </div>
      ) : (
        <ul className="mt-5 space-y-5">
          {results.map((r) => (
            <li key={r.key} className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-white">{r.label}</div>
                <div className="text-xs text-white/45">{r.detail}</div>
              </div>
              {r.hidden ? (
                <span className="text-xs text-white/40 text-right">unter {MIN_GROUP}, nicht angezeigt</span>
              ) : (
                <span className="font-display font-bold text-2xl tabular-nums text-white whitespace-nowrap">
                  {r.display}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {!resultsLocked && results.some((r) => r.group) && (
        <p className="mt-5 text-xs leading-relaxed text-white/40">
          Ergebnisse kleiner Gruppen erscheinen erst ab {MIN_GROUP}, damit niemand einzeln erkennbar ist. Den Link gibt
          es nur einmal für die ganze Mannschaft, nicht pro Spieler.
        </p>
      )}

      <div className="mt-auto pt-6">
        {perspective === "unternehmen" &&
          (open ? (
            <form onSubmit={submit} className="rounded-2xl border border-white/10 p-4 space-y-3" noValidate>
              <div className="flex flex-col sm:flex-row gap-3">
                {results.length > 1 && (
                  <select
                    value={selected}
                    onChange={(e) => setKey(e.target.value)}
                    aria-label="Art des Ergebnisses"
                    className="flex-1 rounded-xl border border-white/12 bg-navy-950 px-3 py-2.5 text-sm text-white outline-none focus:border-brand-blue/60"
                  >
                    {results.map((r) => (
                      <option key={r.key} value={r.key} className="bg-navy-950">
                        {r.label}
                      </option>
                    ))}
                  </select>
                )}
                <input
                  type="number"
                  min={1}
                  max={99}
                  step={1}
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value);
                    setError("");
                  }}
                  aria-label="Anzahl"
                  className="w-full sm:w-28 rounded-xl border border-white/12 bg-white/[0.03] px-3 py-2.5 text-sm text-white outline-none focus:border-brand-blue/60"
                />
              </div>
              {error && <p className="text-xs text-brand-orange">{error}</p>}
              <div className="flex gap-2">
                <button type="submit" className={BTN_SMALL}>
                  <Check className="w-3.5 h-3.5" />
                  Melden
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    setError("");
                  }}
                  className="rounded-full px-4 py-2 text-xs font-semibold text-white/50 hover:text-white transition-colors"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          ) : (
            <button type="button" onClick={() => setOpen(true)} className={BTN_SMALL}>
              <Plus className="w-3.5 h-3.5" />
              Ergebnis melden
            </button>
          ))}
        {perspective === "verein" && !resultsLocked && (
          <p className="text-xs text-white/40">Von {sponsor.name} für den Verein freigegeben.</p>
        )}
      </div>
    </Panel>
  );
};

export const DemoDashboard = ({ data, onPerspective, onReport, onGoto, lastEntry }) => {
  const [reportHint, setReportHint] = useState(false);
  const { sponsor, setup, perspective } = data;
  const lastGame = lastEntry ? data.games.find((g) => g.n === lastEntry.n) : null;
  const hasEffects = data.qr || data.channels.length || data.results.length;
  const resultsWide = !data.channels.length;

  return (
    <div className="space-y-6">
      {lastEntry && lastGame && <NewEntryBanner entry={lastEntry} game={lastGame} />}

      <Panel className="overflow-hidden p-6 md:p-9">
        <div className="pointer-events-none absolute -top-24 -right-16 w-80 h-80 rounded-full bg-brand-blue/15 blur-[110px]" />
        <div className="pointer-events-none absolute -bottom-28 -left-20 w-72 h-72 rounded-full bg-brand-orange/10 blur-[110px]" />
        <div className="relative grid gap-8 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <Kicker>Sponsoring-Dashboard · {SEASON.label}</Kicker>
            <h3 className="mt-3 font-display font-bold text-3xl md:text-4xl leading-tight text-white">
              {sponsor.name} <span className="text-white/30">×</span> {CLUB.name}
            </h3>
            <div className="mt-5 flex flex-wrap gap-2">
              <Chip>{euro(setup.volume)}</Chip>
              <Chip>{data.activeBlocks.length} Leistungen</Chip>
              <Chip>Schwerpunkt {FOCUS_BY_ID[setup.focus].label}</Chip>
              <Chip>Beispieldaten</Chip>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="flex items-baseline justify-between gap-4 text-sm">
              <span className="text-white/50">Laufzeit</span>
              <span className="font-semibold tabular-nums text-white">
                {data.passed} von {SEASON.homeGames} Heimspielen
              </span>
            </div>
            <div className="mt-2.5">
              <ProgressLine value={data.progress} />
            </div>
            <div className="mt-2 text-xs text-white/35">{SEASON.range}</div>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Segmented
                size="sm"
                layoutId="demo-perspective"
                label="Ansicht"
                options={PERSPECTIVES}
                value={perspective}
                onChange={onPerspective}
              />
              {perspective === "unternehmen" ? (
                <button type="button" onClick={() => setReportHint((v) => !v)} className={BTN_SMALL}>
                  <FileText className="w-3.5 h-3.5" />
                  Saisonbericht
                </button>
              ) : (
                <button type="button" onClick={() => onGoto("entry")} className={BTN_SMALL}>
                  <Smartphone className="w-3.5 h-3.5" />
                  Spieltag eintragen
                </button>
              )}
            </div>
            {reportHint && perspective === "unternehmen" && (
              <p className="mt-3 text-xs leading-relaxed text-white/45">
                Im echten Konto entsteht daraus ein PDF, zum Beispiel für die Geschäftsführung oder die
                Steuerberatung.
              </p>
            )}
          </div>
        </div>
      </Panel>

      <Legend />

      <Highlights items={data.highlights} />

      <LevelTitle no="1" title="Geliefert" text="Was vereinbart war und was davon bisher erbracht ist." />
      <Delivered data={data} />
      {data.fotos && <Photos fotos={data.fotos} perspective={perspective} />}

      <LevelTitle no="2" title="Gesehen" text="Wer das Engagement vor Ort und online wahrgenommen hat." />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <Spectators data={data} />
        <Contacts contacts={data.contacts} />
        {data.social && <Social social={data.social} />}
      </div>

      <LevelTitle no="3" title="Bewirkt" text="Was das Sponsoring ausgelöst hat." />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {data.qr && <QrScans qr={data.qr} />}
        {data.channels.length > 0 && (
          <Channels channels={data.channels} clicks={data.clicks} wide={!data.results.length} />
        )}
        {data.results.length > 0 && <Results data={data} onReport={onReport} wide={resultsWide} />}
        {!hasEffects && (
          <Panel className="lg:col-span-12 p-6 md:p-8">
            <p className="max-w-xl text-sm leading-relaxed text-white/55">
              In diesem Setup gibt es noch keine Messwerkzeuge. Schaltet im Setup QR-Codes, die Website-Nennung oder
              einen Link an, dann seht ihr hier, was das Sponsoring auslöst.
            </p>
            <button type="button" onClick={() => onGoto("setup")} className={`${BTN_SMALL} mt-5`}>
              Setup anpassen
            </button>
          </Panel>
        )}
      </div>

      <p className="pt-4 text-xs leading-relaxed text-white/30">
        Alle Werte sind Beispieldaten für einen Fußballverein der Kreisliga mit rund 150 Zuschauern je Heimspiel. Sie
        sind bewusst realistisch gewählt, nicht geschönt.
      </p>
    </div>
  );
};
